/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  DataGridPro,
  GridColDef,
  GridColumnResizeParams,
  GridFilterModel,
  gridPageCountSelector,
  gridPageSelector,
  GridRowModel,
  GridRowsProp,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
  GridColumnVisibilityModel
} from '@mui/x-data-grid-pro'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { Pagination } from '@mui/material'
import { UseReactToPrintFn } from 'react-to-print'
import { useTranslation } from 'react-i18next'
import styles from './Table.module.scss'
import { tableStyles, paginationStyles } from './Table.styles'
import CustomGridToolbar from './components/CustomGridToolbar/CustomGridToolbar'
import { FILTER_KEYS, TABLE_KEYS } from './models/Filter.model'
import CustomFilterPanel from './components/CustomFilterPanel/CustomFilterPanel'
import { popperStyles as selectPopperStyles } from '../Select/Select.styles'
import { floatingSurface } from '@/shared/styles/floatingSurface.styles'
import {
  primaryColors,
  transparentColors,
  whiteShades
} from '@/assets/styles/helpers/colors.styles'

interface TableProps {
  columns: GridColDef[]
  columnsModel?: string[]
  hideColumnFilter?: boolean
  hideRowFilter?: boolean
  filtersList?: FILTER_KEYS[]
  tableKey: TABLE_KEYS
  initialRows: GridRowsProp
  filterModel: GridFilterModel
  handleOnAddItem?: () => void
  handleOnDeleteItems?: (ids: string[]) => void
  hideToolbar?: boolean
  hideCheckboxSelection?: boolean
  hideFooter?: boolean
  hideActionsButtons?: boolean
  setSelectedExternalRowIds?: (ids: string[]) => void
  selectedExternalRowIds?: string[]
  handlePrint?: UseReactToPrintFn
  onRowsChange?: (rows: GridRowModel[]) => void
  disablePagination?: boolean
  columnVisibilityModel?: Record<string, boolean>
  isLoading?: boolean
}

function PageNumbers() {
  const apiRef = useGridApiContext()
  const page = useGridSelector(apiRef, gridPageSelector) // 0-based
  const pageCount = useGridSelector(apiRef, gridPageCountSelector) // total pages

  return (
    <Pagination
      count={pageCount}
      page={page + 1}
      onChange={(_e, value) => apiRef.current.setPage(value - 1)}
      sx={paginationStyles}
    />
  )
}

function Table(props: TableProps) {
  const {
    columns,
    columnsModel,
    hideColumnFilter,
    hideRowFilter,
    filtersList,
    tableKey,
    initialRows,
    handleOnAddItem = () => null,
    handleOnDeleteItems = () => null,
    hideToolbar,
    hideCheckboxSelection,
    hideFooter,
    hideActionsButtons,
    setSelectedExternalRowIds,
    selectedExternalRowIds,
    handlePrint,
    onRowsChange,
    disablePagination = false,
    columnVisibilityModel,
    isLoading = false
  } = props

  const { t } = useTranslation()
  const apiRef = useGridApiRef()
  const [restoreColumnSizes, setRestoreColumnSizes] = useState(false)
  const savedColumnWidths = useRef<Record<string, number>>({})
  const hasManuallyResized = useRef(false)
  const resizeRAF = useRef<number | null>(null)
  const [columnResizeCount, setColumnResizeCount] = useState(0)
  const [rows, setRows] = useState<GridRowsProp>(initialRows)

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>(
    selectedExternalRowIds || []
  )

  const resetSelectedRowIds = useCallback(() => {
    setSelectedRowIds([])
    if (setSelectedExternalRowIds) {
      setSelectedExternalRowIds([])
    }
  }, [setSelectedExternalRowIds])

  // Cleanup resize RAF on unmount
  useEffect(() => {
    return () => {
      if (resizeRAF.current) {
        cancelAnimationFrame(resizeRAF.current)
      }
    }
  }, [])

  // Sync internal rows state with initialRows prop
  useEffect(() => {
    setRows(initialRows)
  }, [initialRows])

  useEffect(() => {
    if (!apiRef.current || !onRowsChange) return

    const handleRowUpdate = () => {
      if (!apiRef.current) return
      const allRows = Array.from(apiRef.current.getRowModels().values())

      setRows(prevRows => {
        const prevSerialized = JSON.stringify(prevRows)
        const newSerialized = JSON.stringify(allRows)

        if (prevSerialized !== newSerialized) {
          onRowsChange(allRows)
          return allRows
        }
        return prevRows
      })
    }

    return apiRef.current.subscribeEvent('rowsSet', handleRowUpdate)
  }, [apiRef, onRowsChange])

  useEffect(() => {
    if (selectedExternalRowIds !== undefined) {
      setSelectedRowIds(selectedExternalRowIds)
    }
  }, [selectedExternalRowIds])

  const handleOnAddItemStable = useCallback(handleOnAddItem, [handleOnAddItem])
  const handleOnDeleteItemsStable = useCallback(handleOnDeleteItems, [
    handleOnDeleteItems
  ])

  const autosizeOptionsValue = useMemo(
    () => ({
      columns: columnsModel,
      expand: true
    }),
    [columnsModel]
  )

  const handleRestoreColumnSizes = useCallback((value: boolean) => {
    setRestoreColumnSizes(value)
    if (!value) {
      // Reset saved widths when user clicks restore button
      savedColumnWidths.current = {}
      hasManuallyResized.current = false
      setColumnResizeCount(c => c + 1) // Trigger re-computation to restore flex columns
    }
  }, [])

  const toolbarSlotProps = useMemo(
    () =>
      ({
        apiRef,
        restoreColumnSizes,
        setRestoreColumnSizes: handleRestoreColumnSizes,
        hideColumnFilter,
        hideRowFilter,
        handleOnAddItem: handleOnAddItemStable,
        handleOnDeleteItems: handleOnDeleteItemsStable,
        selectedRowIds,
        handlePrint,
        hideActionsButtons,
        resetSelectedRowIds,
        autosizeOptions: { expand: true }
      }) as any,
    [
      apiRef,
      restoreColumnSizes,
      handleRestoreColumnSizes,
      hideColumnFilter,
      hideRowFilter,
      handleOnAddItemStable,
      handleOnDeleteItemsStable,
      selectedRowIds,
      handlePrint,
      hideActionsButtons,
      resetSelectedRowIds
    ]
  )

  const filterPanelSlotProps = useMemo(
    () => ({
      filtersList: filtersList || [],
      tableKey
    }),
    [filtersList, tableKey]
  )

  const slotPropsValue = useMemo(
    () =>
      ({
        toolbar: toolbarSlotProps,
        filterPanel: filterPanelSlotProps,
        panel: {
          sx: {
            '& .MuiDataGrid-paper': {
              ...floatingSurface
            },
            '& .MuiDataGrid-columnsManagement': {
              color: primaryColors.white,
              '& .MuiCheckbox-root': {
                color: primaryColors.orange
              },
              '& .MuiCheckbox-root.Mui-checked': {
                color: primaryColors.orange
              },
              '& .MuiFormControlLabel-label': {
                color: primaryColors.white
              }
            },
            '& .MuiDataGrid-columnsManagementHeader': {
              display: 'none'
            },
            '& .MuiButtonBase-root.MuiButton-text': {
              display: 'none'
            },
            '& .MuiDataGrid-columnsManagementFooter': {
              borderTop: 'none',
              paddingRight: '3rem',
              '& .MuiCheckbox-root': {
                color: primaryColors.orange
              },
              '& .MuiCheckbox-root.Mui-checked': {
                color: primaryColors.orange
              },
              '& .MuiFormControlLabel-label': {
                color: primaryColors.white
              }
            }
          }
        },
        columnsManagement: {
          getTogglableColumns: (cols: GridColDef[]) =>
            cols
              .filter(
                col => col.field !== '__check__' && col.hideable !== false
              )
              .map(col => col.field)
        },
        loadingOverlay: {
          variant: 'skeleton' as const,
          noRowsVariant: 'skeleton' as const
        },
        basePopper: {
          placement: 'bottom-start' as const
        },
        pagination: {
          ActionsComponent: PageNumbers,
          SelectProps: {
            IconComponent: ExpandMoreIcon,
            MenuProps: {
              anchorOrigin: {
                vertical: 'top' as const,
                horizontal: 'left' as const
              },
              transformOrigin: {
                vertical: 'bottom' as const,
                horizontal: 'left' as const
              },
              PaperProps: { sx: selectPopperStyles }
            }
          }
        }
      }) as any,
    [toolbarSlotProps, filterPanelSlotProps]
  )

  const sxValue = useMemo(
    () => ({
      ...tableStyles,
      width: '100%',
      minWidth: 0,
      '& .MuiDataGrid-main': {
        minWidth: 0
      },
      '--DataGrid-t-color-background-base': `${transparentColors.transparent} !important`,
      '--DataGrid-t-cell-background-pinned': `${transparentColors.transparent} !important`,
      '--DataGrid-t-header-background-base': `${transparentColors.transparent} !important`,
      '--DataGrid-t-color-border-base': `${whiteShades.shade1} !important`,
      '--DataGrid-t-color-background-overlay': `${transparentColors.transparent} !important`
    }),
    []
  )

  const slotsValue = useMemo(
    () => ({
      toolbar: CustomGridToolbar as any,
      filterPanel: CustomFilterPanel as any
    }),
    []
  )

  const initialStateValue = useMemo(
    () => ({
      pagination: { paginationModel: { pageSize: 10 } },
      pinnedColumns: { right: ['action'] }
    }),
    []
  )

  const [visibilityModel, setVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      id: false,
      ...(columnVisibilityModel ?? {})
    })

  // Track previous columnVisibilityModel to detect actual changes
  const prevColumnVisibilityModel = useRef<string>(
    JSON.stringify(columnVisibilityModel ?? {})
  )

  useEffect(() => {
    const currentModel = JSON.stringify(columnVisibilityModel ?? {})
    // Only update if the prop actually changed (not just a new object reference)
    if (prevColumnVisibilityModel.current !== currentModel) {
      prevColumnVisibilityModel.current = currentModel
      setVisibilityModel(prev => ({
        ...prev,
        ...(columnVisibilityModel ?? {})
      }))
    }
  }, [columnVisibilityModel])

  // Use onColumnWidthChange which fires AFTER resize is complete with the final width
  const handleColumnWidthChange = useCallback(
    (params: GridColumnResizeParams) => {
      hasManuallyResized.current = true
      savedColumnWidths.current[params.colDef.field] = params.width

      // Batch state updates using requestAnimationFrame for smoother performance
      if (resizeRAF.current) {
        cancelAnimationFrame(resizeRAF.current)
      }
      resizeRAF.current = requestAnimationFrame(() => {
        setRestoreColumnSizes(true)
        setColumnResizeCount(c => c + 1)
        resizeRAF.current = null
      })
    },
    []
  )

  // Modify columns to remove flex and set fixed width for manually resized columns
  const processedColumns = useMemo(() => {
    if (Object.keys(savedColumnWidths.current).length === 0) {
      return columns
    }
    return columns.map(col => {
      const savedWidth = savedColumnWidths.current[col.field]
      if (savedWidth) {
        // Remove flex and set fixed width for manually resized columns
        const { flex, ...rest } = col
        return { ...rest, width: savedWidth }
      }
      return col
    })
  }, [columns, columnResizeCount]) // columnResizeCount triggers re-computation when columns are resized

  return (
    <div className={`${styles.tableContainer}`}>
      <DataGridPro
        autosizeOptions={autosizeOptionsValue}
        apiRef={apiRef}
        className={`${styles.dataGrid}`}
        rows={rows}
        columns={processedColumns}
        columnVisibilityModel={visibilityModel}
        onColumnVisibilityModelChange={model => {
          setVisibilityModel(model)
        }}
        localeText={{
          columnsManagementShowHideAllText: t('TABLE.COLUMNS.SHOW_HIDE_ALL')
        }}
        initialState={initialStateValue}
        checkboxSelection={!hideCheckboxSelection || false}
        disableRowSelectionOnClick
        hideFooter={hideFooter || false}
        {...(!disablePagination && {
          pagination: true
        })}
        disableVirtualization
        rowHeight={58}
        columnHeaderHeight={32}
        onColumnWidthChange={handleColumnWidthChange}
        showToolbar={!hideToolbar}
        slots={slotsValue}
        sx={sxValue}
        pageSizeOptions={[10, 25, 50]}
        loading={isLoading}
        slotProps={slotPropsValue}
        disableColumnResize={false}
        ignoreDiacritics
        rowSelectionModel={{ type: 'include', ids: new Set(selectedRowIds) }}
        onRowSelectionModelChange={newSelection => {
          let selectedIds: string[]
          if (newSelection.type === 'exclude') {
            const excludedIds = newSelection.ids
            selectedIds = rows
              .map(row => String(row.id))
              .filter(id => !excludedIds.has(id))
          } else {
            selectedIds = [...newSelection.ids] as string[]
          }
          setSelectedRowIds(selectedIds)
          if (setSelectedExternalRowIds) {
            setSelectedExternalRowIds(selectedIds)
          }
        }}
        processRowUpdate={newRow => {
          const updatedRows = rows.map(r => (r.id === newRow.id ? newRow : r))
          setRows(updatedRows)
          onRowsChange?.(updatedRows)
          return newRow
        }}
        onCellClick={params => {
          if (params.isEditable) {
            apiRef?.current?.startCellEditMode({
              id: params.id,
              field: params.field
            })
          }
        }}
      />
    </div>
  )
}

export default Table
